/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react"
import { useHookstate } from "@hookstate/core";
import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import React from "react";
import { Link, useParams } from "react-router-dom";
import { Catalog } from "../../../hooks/useCatalogState";
import { ProductCategory, ProductDepartment, ProductSubCategory, useInventoryState } from "../../../hooks/useInventoryState";
import { useThemeState } from "../../../hooks/useThemeState";
import { SubCategoryControl } from "./SubCategoryControl";
import { useIsDesktopMediaQuery } from "../../../hooks/useIsDesktopMediaQuery";
import { FolderClosed, FolderOpen } from "lucide-react";
import { useTailwindMediaQueries } from "@/hooks/useTailwindMediaQueries";

//-------------------------------------------------------------------------------------------------------
export type CategoryControlProps = {
    catalog: Catalog;
    department: ProductDepartment;
    category: ProductCategory;
    isParentFilterActive: boolean;
}
export const CategoryControl: React.FC<CategoryControlProps> = props => {
    const mediaQueries = useTailwindMediaQueries();
    const theme = useThemeState().get().theme;
    const inventoryState = useInventoryState();
    const { categoryid } = useParams();
    
    let isFilterActiveState = useHookstate<boolean>(false);
    let isFilterActive = isFilterActiveState.get();
    let url = `/inventory/${props.catalog.Id}/${props.department.Id}/${props.category.Id}`;
    const hasSubCategories = (props.category.SubCategories && props.category.SubCategories.length > 0) ?? false;
    
    // Check if this category is selected based on URL parameters
    const isSelected = (categoryid === props.category.Id);
    
    const isDesktop = useIsDesktopMediaQuery();

    return (
        <React.Fragment key={props.category.Id}>
            <div
                css={css`
                    display:flex;
                    align-items:center;
                    margin:0 0 0 8px;
                    padding:.2rem 0;
                `}
                onClick={async () => {
                    return Promise.resolve().then(() => {
                        isFilterActive = (!isFilterActive);
                        isFilterActiveState.set(isFilterActive);
                    });
                }}
            >
                {!hasSubCategories && (<>
                    <Link
                        to={url}
                        css={css`
                            flex:1 1 0;
                            cursor:pointer;
                            color: ${isSelected ? theme.palette.siteSideBarContrastText : theme.palette.siteSideBarText};
                            text-decoration: none;
                            &:hover {
                                text-decoration: underline;
                            }`}
                        onClick={() => {
                            return Promise.resolve().then(() => {
                                // Clear search text when category is clicked since they're mutually exclusive
                                inventoryState.clearSearchTextAsync();
                                if (!isDesktop) {
                                    inventoryState.setSidebarExpanded(false);
                                }
                            })
                        }}
                    >
                        <span css={css`padding: ${mediaQueries.desktop ? '0' : '4px'} 0 ${mediaQueries.desktop ? '0' : '4px'} 4px;font-size:${mediaQueries.desktop ? '.8rem' : '1rem'};`}>{props.category.Name}</span>
                    </Link>
                </>)}
                {hasSubCategories && (<>
                    <div
                        css={css`
                            flex:1 1 0;
                            display:flex;
                        `}>
                        <div
                            css={css`
                                flex:1 1 0;
                                cursor:pointer;
                                text-indent: -.5em;
                                display:flex;
                                align-items: center;
                            `}>
                            <div css={css`flex: 0 0 auto;display:flex;align-items:center;`}>
                                {!isFilterActive && (<FolderClosed size={mediaQueries.desktop ? 16 : 22} />)}
                                {isFilterActive && (<FolderOpen size={mediaQueries.desktop ? 16 : 22} />)}
                            </div>
                            <div css={css`flex: 1 1 0;padding:${mediaQueries.desktop ? '0' : '4px'} 14px;font-size:${mediaQueries.desktop ? '.8rem' : '1rem'};`}>
                                {props.category.Name}
                            </div>
                        </div>
                            
                        {props.category.SubCategories && props.category.SubCategories.length > 0 &&
                            <div
                                css={css`
                                    flex:0 0 auto;
                                    color:${theme.palette.siteSideBarText};
                                    display:flex;
                                    align-items:center;
                                `}>
                                {!isFilterActive &&
                                    <KeyboardArrowRight fontSize="small" />
                                }
                                {isFilterActive &&
                                    <KeyboardArrowDown fontSize="small" />
                                }
                            </div>
                        }
                    </div>
                </>)}
                


            </div>
            {isFilterActive && props.category.SubCategories && props.category.SubCategories.map((productSubCategory: ProductSubCategory) => (
                <SubCategoryControl
                    key={productSubCategory.Id}
                    catalog={props.catalog}
                    department={props.department}
                    category={props.category}
                    subCategory={productSubCategory}
                    isParentFilterActive={props.isParentFilterActive && isFilterActive} />
            ))}
        </React.Fragment>
    );
}